import { Button, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useResolvedPath } from 'react-router-dom';
import { Info, Warning } from '../../assets/icons';
import useMediaQuery from '../../Hooks/useMediaQuery';
import useSecondaryMarketEligibility from '../../Hooks/useSecondaryMarketEligibility';
import { cardData } from '../../pages/PropertyListing/SMVacationData';
import { maxShareCalculatorWithNoOfShare } from '../../utils/maxShareCalculator';
import { toastNotify } from '../../utils/toastNotify';
import { getUsCurrencyFormat, getUsPercentageFormat } from '../../utils/usCurrencyFormatter';
import SharePriceChart from '../Charts/SharePriceChart';
import SecondaryMarketIdentityUpload from '../SecondaryMarketIdentityUpload/SecondaryMarketIdentityUpload';
import './PropertyTradeCard.scss';
import { AML_DOCUMENT_PENDING, COMPLETE_VERIFICATION_FIRST, ENTITY_SECONDARY_MARKET_MESSAGE, KYC_NOT_VERIFIED_MESSAGE, WARN_DWOLLA_PENDING, checkOnboardingComplete } from '../../utils/constant';

const PropertyTradeCard = (props) => {
  // Use isSecondaryMarketPaused from API response
  const state = useSelector((state) => state.propertyDetails);
  const isSecondaryMarketPaused = state?.data?.data?.isSecondaryMarketPaused === true;
  // Tooltip message for paused secondary market
  const secondaryPausedTooltip = isSecondaryMarketPaused
    ? 'The Realbricks secondary market trading is temporarily paused.'
    : '';
  // Helper for tooltip container (fixes tooltip not showing on disabled button)
  const getPopupContainer = (trigger) => trigger.parentNode;
  const { onNotifyClick, setIsSellOpen } = props;
  const notifyLoading = useSelector((state) => state.notifyMeSlice.loading);
  const screenMD = useMediaQuery('(min-width: 1000px)');
  const { pathname } = useResolvedPath();
  const userData = useSelector((state) => state.userData);
  const navigate = useNavigate();
  const {
    data: {
      data: {
        lastPrice = 0,
        totalBids = 0,
        totalAsks = 0,
        lowestAsk = 0,
        highestBid = 0,
        appreciation = 0,
        tenantStatus: { dividendYield = null } = {},
        finance: { perSharePrice = 0 } = {},
        chartData = [],
        userOpenOrders = 0,
        totalShare = 0,
        userPurchaseDetail: { noOfShares = 0 } = {},
        isSellOrdersTradable = false,
      } = {},
    } = {},
  } = state;
  const currentChartData = chartData.map((x, i) => ({ day: i + 1, price: x }));
  const cardInfoData = useMemo(() => {
    if (state?.data?.data) {
      // deep copy of object so value updates on data change
      const arr = JSON.parse(JSON.stringify(cardData.cardInfoData));
      return arr.map((x) => {
        let value = '';
        switch (x.value) {
          case 'lastPrice':
            value = getUsCurrencyFormat(Number(lastPrice) || 0) || 0;
            break;

          case 'allTime':
            value = getUsCurrencyFormat((100 * (Number(lastPrice) - Number(perSharePrice))) / Number(perSharePrice) || 0);
            value = Number(value) > 0 ? `+${value}` : value;
            break;
          case 'dividendYield': {
            if (dividendYield === null || dividendYield === undefined) {
              value = 'NA';
              x.suffix = '';
            } else {
              const numVal = Number(dividendYield);
              if (!isNaN(numVal)) {
                value = getUsCurrencyFormat(numVal || 0);
              } else {
                // Backend returned a pre-formatted string (e.g. "10% - 20%")
                value = String(dividendYield);
                x.suffix = ''; // suffix already included in the string
              }
            }
            break;
          }
          default:
            break;
        }
        if (value) {
          if (x.prefix) {
            x.value = `${x.prefix}${value}`;
          } else if (x.suffix) {
            x.value = `${value}${x.suffix}`;
          } else {
            x.value = value;
          }
        }
        return x;
      });
    }
    return [];
  }, [state?.data?.data]);
  const activityList = (
    <>
      <div className='activity__list'>
        <div className='activity__list__item'>
          <span className='activity__list__item__label'>Best ask {!screenMD ? `(${totalAsks || 0})` : ''}</span>
          <span className='activity__list__item__value'>${getUsCurrencyFormat(Number(lowestAsk) || 0) || 0}</span>
        </div>
        <div className='activity__list__item'>
          <span className='activity__list__item__label'>Best bid {!screenMD ? `(${totalBids || 0})` : ''}</span>
          <span className='activity__list__item__value'>${getUsCurrencyFormat(Number(highestBid) || 0) || 0}</span>
        </div>
      </div>
      <div className='text-center mt-24'>
        <Link className='text-copper fz-16 fw-600' to={pathname + '/details'}>
          View details
        </Link>
      </div>
    </>
  );

  const isBuyDisabled = useMemo(() => {
    const sharesRemainingToPurchase = (Number(totalShare) * 9.8) / 100 - Number(noOfShares) - Number(state?.data?.data?.requestedShares);
    return Number(sharesRemainingToPurchase) < 1;
  }, [noOfShares, totalShare]);
  // (already declared above, do not redeclare)

  const { isSellEligible, sellIneligibleMsg, isEntityRestricted } = useSecondaryMarketEligibility();
  const [showIdentityModal, setShowIdentityModal] = useState(false);

  const handleBuyClick = () => {
    if (isEntityRestricted) {
      toastNotify('error', ENTITY_SECONDARY_MARKET_MESSAGE);
      return;
    }
    setIsSellOpen('1');
    Object.keys(userData?.userData).length > 0
      ? userData?.userData?.isKYCVerified && userData?.userData?.isDwollaVerified
        ? !userData?.userData?.isAMLApproved && userData?.userData?.isDocRequiredForAml
          ? toastNotify('error', <AML_DOCUMENT_PENDING onOpenModal={() => setShowIdentityModal(true)} />)
          : props.clickAction()
        : !userData?.userData?.isKYCVerified
        ? checkOnboardingComplete(userData?.userData, userData?.counter)
          ? toastNotify('error', KYC_NOT_VERIFIED_MESSAGE)
          : toastNotify('error', <COMPLETE_VERIFICATION_FIRST navigate={navigate}/>)
        : toastNotify('error', WARN_DWOLLA_PENDING)
      : navigate('/login');
  };

  const handleSellClick = () => {
    if (isEntityRestricted) {
      toastNotify('error', ENTITY_SECONDARY_MARKET_MESSAGE);
      return;
    }
    setIsSellOpen('2');
    Object.keys(userData?.userData).length > 0
      ? userData?.userData?.isKYCVerified && userData?.userData?.isDwollaVerified
        ? !userData?.userData?.isAMLApproved && userData?.userData?.isDocRequiredForAml
          ? toastNotify('error', <AML_DOCUMENT_PENDING onOpenModal={() => setShowIdentityModal(true)} />)
          : props.clickAction()
        : !userData?.userData?.isKYCVerified
        ? checkOnboardingComplete(userData?.userData, userData?.counter)
          ? toastNotify('error', KYC_NOT_VERIFIED_MESSAGE)
          : toastNotify('error', <COMPLETE_VERIFICATION_FIRST navigate={navigate}/>)
        : toastNotify('error', WARN_DWOLLA_PENDING)
      : navigate('/login');
  };

  return (
    <>
      <div className={Number(userOpenOrders) > 0 ? 'trade-initial open' : 'trade-initial'}>
        {Number(userOpenOrders) > 0 ? (
          <div className='open-orders'>
            <Warning marginRight='10px' />
            <span>{userOpenOrders} open orders</span>
            <Link to='/portfolio#open-orders' className='ml-auto fw-600'>
              View
            </Link>
          </div>
        ) : (
          ''
        )}
        {!screenMD ? (
          <>
            {isEntityRestricted ? null : state?.data?.data?.tag !== 'Coming Soon' ? (
              <span>
                <Tooltip
                  title={secondaryPausedTooltip}
                  mouseEnterDelay={0.1}
                  getPopupContainer={getPopupContainer}
                >
                  <span>
                    <Button className='w-100 mb-32' type='primary' onClick={() => props.clickAction()} disabled={isSecondaryMarketPaused || state?.data?.data?.tag==='Funded'}>
                      {cardData.tradeLabel}
                    </Button>
                  </span>
                </Tooltip>
              </span>
            ) : (
              <Button
                className='w-100 mb-24'
                onClick={() => onNotifyClick({ id: state?.data?.data?.propertyId, isSecondary: state?.data?.data?.isSecondary })}
                loading={notifyLoading}
              >
                Notify me
              </Button>
            )}
          </>
        ) : (
          <>
            {isEntityRestricted ? null : state?.data?.data?.tag !== 'Coming Soon' ? (
              <div className='mb-32 d-flex flex-column g-16'>
                <Tooltip
                  title={secondaryPausedTooltip}
                  mouseEnterDelay={0.1}
                  getPopupContainer={getPopupContainer}
                >
                  <span>
                    <Button
                      className='w-100'
                      type='primary'
                      onClick={handleBuyClick}
                      disabled={
                        isSecondaryMarketPaused ||
                        isBuyDisabled ||
                        maxShareCalculatorWithNoOfShare(
                          state?.data?.data?.totalShare,
                          state?.data?.data?.requestedShares,
                          state?.data?.data?.userPurchaseDetail?.noOfShares
                        ) < 1 || state?.data?.data?.tag==='Funded'
                      }
                    >
                      {cardData.buyLabel}
                    </Button>
                  </span>
                </Tooltip>
                {props.sellLabel && (
                  <Tooltip
                    title={
                      !Number(noOfShares)
                        ? ''
                        : !isSellEligible
                        ? sellIneligibleMsg
                        : !isSellOrdersTradable
                        ? 'You cannot create this sell order because some or all of your shares are already placed in open sell orders.'
                        : secondaryPausedTooltip
                    }
                    mouseEnterDelay={0.1}
                    getPopupContainer={getPopupContainer}
                  >
                    <span>
                      <Button
                        className='w-100'
                        onClick={handleSellClick}
                        disabled={isSecondaryMarketPaused || !Number(noOfShares) || state?.data?.data?.tag==='Funded' || !isSellOrdersTradable || !isSellEligible}
                      >
                        {cardData.sellLabel}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </div>
            ) : (
              <Button
                className='w-100 mb-24'
                onClick={() => onNotifyClick({ id: state?.data?.data?.propertyId, isSecondary: state?.data?.data?.isSecondary })}
                loading={notifyLoading}
              >
                Notify me
              </Button>
            )}
          </>
        )}
        <div className='data-list'>
          {cardInfoData.map((data, index) => {
            return (
              <div className='data-list__item' key={index}>
                <Tooltip title={data.tooltip}>
                  <span className={data.tooltip ? 'data-list__item__label tooltip' : 'data-list__item__label'}>
                    {data.label} {data.tooltip && <Info marginLeft='2px' verticalAlign='sub' />}
                  </span>
                </Tooltip>
                <span className='data-list__item__value'>{data.value}</span>
              </div>
            );
          })}
        </div>
        {!screenMD && <hr className='line' />}
        {!screenMD ? (
          activityList
        ) : (
          <div className='activity'>
            <p className='fz-12 text-taupe-80 mb-0'>Activity</p>
            <div className='fz-20 fw-600'>
              <span>{totalBids || 0} Bids</span>
              <span className='text-taupe-40'> • </span>
              <span>{totalAsks || 0} Asks</span>
            </div>
          </div>
        )}
      </div>
      {screenMD && (
        <div className='trade-graph'>
          <div className='graph-data'>
            <div className='numbers'>
              <p className='fz-12 text-taupe-80 mb-4'>Difference per share</p>
              <p className={`number ${Number(appreciation) > 0 ? 'growth' : 'decline'}`}>{getUsPercentageFormat(Number(appreciation) || 0) || 0}</p>
              <p className='fz-12 text-taupe-60 mb-0'>{props?.graphData?.dataDuration}</p>
            </div>
            <div className='image'>
              {/* <img src={Images.smallGraph} alt='' /> */}
              <SharePriceChart data={currentChartData} color={Number(appreciation) > 0 ? '#2aa47f' : '#dd0d33'} />
            </div>
          </div>
          {activityList}
        </div>
      )}
      <SecondaryMarketIdentityUpload
        visible={showIdentityModal}
        onClose={() => setShowIdentityModal(false)}
        onSuccess={() => setShowIdentityModal(false)}
      />
    </>
  );
};

export default PropertyTradeCard;
